/// <amd-module name="Dashboards/WidgetContracts/WidgetProps" />
import { IVssContextualMenuItem } from "VSSUI/Components/VssContextualMenu";

/** Size, expressed in pixels */
export interface WidgetSize {
    width: number;
    height: number;
}

/** A best-effort description of the team context which the widget is currently being viewed with. */
export interface WidgetTeamContext {
    id: string;
    name: string;
}

/**
 * versioning for an artifact as described at: http://semver.org/, of the form major.minor.patch.
 */
export interface SemanticVersion {
    /**
     * Major version when you make incompatible API changes
     */
    major: number;
    /**
     * Minor version when you add functionality in a backwards-compatible manner
     */
    minor: number;
    /**
     * Patch version when you make backwards-compatible bug fixes
     */
    patch: number;
}

/**
 * Describes the circumstances a widget is being rendered under.
 */
export const enum WidgetPresentationMode {
    /** Default Scenario of a widget contained from a dashboard view  */
    dashboardView = "dashboard-view",

    /** A preview of Widget during configuration. In this mode, a widget should bypass behaviors such as animations, with a focus on speed to allow user to iteratively configure and see impacts of changes. */
    dashboardConfigPreview = "dashboard-config-preview",

    /** A widget hosted in a dashboard under edit mode. Widgets in this mode should behave in same fashion as in dashboard view mode.  */
    dashboardEditView = "dashboard-edit-view",

    /** Lightbox view. */
    lightboxView = "lightbox-view",

    /** Embedded as part of a non-dashboard experience. */
    embeddedView = "embedded-View"
}

/** Configuration description of a Widget. */
export interface WidgetConfigurationProps {
    /** Fully qualified Identifier of Widget Contribution. This can be helpful for scenarios such as telemetry, on codepaths shared by different widgets. */
    contributionId: string;

    /** (Optional) Unique identifier of widget Configuration.
     *  Can be absent with widget previews during configuration, as well as widgets hosted on non-configurable "in-situ" experiences.
     */
    id?: string;

    /**
     * User-configurable name of widget. Rendered as title.
     */
    name: string;

    /**
     * Size of Widget, expressed in pixels.
     */
    size: WidgetSize;

    /**
     * Widget-defined Configuration. Supplied by Widget Configuration - default configuration is null.
     */
    settings: string | null;

    /**
     * Widget-defined settings version. Allows widget authors to recognize legacy data formats.
     */
    settingsVersion: SemanticVersion;
}

export enum LoadingStatus {
    /**
     * Initial status of a Widget. Note: a widget *cannot* return back to this state.
     */
    loading,

    /**
     * Status of a widget after it has reached a point of user interactivity.
     * If a widget has an progressively loaded experience, it should report success once initially usable, and internally deal with any secondary failure modes.
     */
    success,

    /**
     * Status of a widget which has clearly failed, and cannot be interacted with as intended.
     */
    failure
}

export interface LoadedEventInfo {
    /**
     * Describes the final outcome of loading.
     */
    outcome: LoadingStatus;

    /**
     * User facing description of failure events.  Presented in error experience AND collected in telemetry.
     */
    outcomeDescription?: string;

    /**
     * Advanced, diagnostic telemetry-only information for failures. Not presented to users.
     */
    extendedDiagnosticDetail?: any;
}

/**
 *  A hint to the widget host to help determine a suitable context menu color over the widget.
 * In future, we will probably ask the widget to pass in the background color, to choose lowest contrast color for the button.
 */
export enum WidgetContextMenuColorMode {
    /** Notifies widget to prefer light colors */
    Default,
    /** Notifies widget to prefer dark colors */
    Dark
}

export interface WidgetBehaviorProps {
    /** Indicates if the Widget should show loading animations. */
    showLoadAnimations: boolean;

    /** Indicates if the widget should prefer opening links in a new tab. */
    openLinksInNewTab: boolean;

    /** Optional prop for ensuring widget title is omitted in cases such as where a widget shares the same title as a parent component, where the title would be redundant.
     *  Consumers are responsible for acting on this hint in the widget and testing the widget. */
    shouldNotShowTitle?: boolean;

    /**
     * Indicates if the widget is rendering in a context where showing modal UI is unsupported.
     * For example, when a widget is rendered in a lightbox, which already is a modal experience,
     * rendering further modal experiences is problematic.
     */
    avoidShowingModalUI?: boolean;
}

/**
 *  Run-time information to help render a Widget. This is provided by a WidgetHost when the widget is rendered.
 */
export interface WidgetRuntimeProps extends WidgetBehaviorProps {
    /** Describes the circumstances the widget is being rendered under - Used for telemetry. */
    presentationMode: WidgetPresentationMode;

    /** Identifies the page the widget is being rendered on. Used for telemetry. */
    parentPageName?: string;

    /** Describes the team context which the widget is currently being viewed with. */
    teamContext: WidgetTeamContext;

    /** Called by Widget to notify WidgetHost that loading is done, and the widget is ready for interaction.
     *  Until this happens, host maintains an overlay experience over the widget.
     *
     *  If no information is passed to describe the event, it is assumed to be a successful load.
     *
     * The first non-loading outcome is the only one which matters. Any subsequent notifications are ignored.
     */
    onWidgetLoaded(loadedEventInfo?: LoadedEventInfo): void;

    /**
     * Called by widget to initiate configuration mode of the widget. Only supported for widgets in dashboardView mode.
     */
    onConfigure(): void;

    /** Called by Widget to describe preferred WidgetHost Context Menu coloring.
     * This method is independent of WidgetLoad event, and can be called as often as needed.
     */
    setMenuColorMode(widgetMenuColorMode: WidgetContextMenuColorMode): void;

    /** Called by Widget to supply custom menu items for rendering in Widget Host Context menu.
     * As widget state changes this can be called again, with subsequent invocations completely replacing prior custom items from the widget.
     */
    setCustomContextMenuItems(customMenuItems: IVssContextualMenuItem[]): void;
}

/**
 * Contract for Rendering a Widget - A union of Configuration and Runtime properties.
 */
export interface WidgetProps extends WidgetConfigurationProps, WidgetRuntimeProps {}
